import React, { useState, useRef, useEffect } from 'react';
import { Sparkles, Send, Loader2, X, Lightbulb, Code } from 'lucide-react';
import { generateAppCode, analyzeAppRequirements, getAppSuggestions } from '../../../services/aiService';

const AIChatPanel = ({ onCodeGenerated, isOpen, onClose }) => {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "👋 Hi! I'm your AI coding assistant. Describe the app you want to build, and I'll generate the code for you. Try something like:\n\n• \"Create a todo list app with add/delete functionality\"\n• \"Build an e-commerce product catalog\"\n• \"Make a contact form with validation\"",
      timestamp: new Date(),
    }
  ]);
  const [input, setInput] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (textareaRef?.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef?.current?.scrollHeight + 'px';
    }
  }, [input]);

  const loadSuggestions = async (category) => {
    try {
      setShowSuggestions(true);
      const appSuggestions = await getAppSuggestions(category);
      setSuggestions(appSuggestions);
    } catch (error) {
      console.error('Error loading suggestions:', error);
    }
  };

  const handleAnalyze = async () => {
    if (!input?.trim() || isAnalyzing) return;

    setIsAnalyzing(true);
    const userMessage = {
      role: 'user',
      content: input,
      timestamp: new Date(),
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInput('');

    try {
      const analysis = await analyzeAppRequirements(input);
      
      const analysisMessage = {
        role: 'assistant',
        content: `📊 **App Analysis**\n\n**Summary:** ${analysis?.summary}\n\n**Complexity:** ${analysis?.complexity}\n\n**Main Features:**\n${analysis?.features?.map(f => `• ${f}`)?.join('\n')}\n\n**Suggested Components:**\n${analysis?.components?.map(c => `• ${c}`)?.join('\n')}\n\n${analysis?.estimatedTime ? `**Estimated Time:** ${analysis?.estimatedTime}\n\n` : ''}${analysis?.recommendations?.length ? `**Recommendations:**\n${analysis?.recommendations?.map(r => `• ${r}`)?.join('\n')}\n\n` : ''}Ready to generate the code? Just ask!`,
        timestamp: new Date(),
        type: 'analysis',
      };

      setMessages(prev => [...prev, analysisMessage]);
    } catch (error) {
      const errorMessage = {
        role: 'assistant',
        content: `⚠️ Error analyzing requirements: ${error?.message}`,
        timestamp: new Date(),
        isError: true,
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleGenerate = async () => {
    if (!input?.trim() || isGenerating) return;

    setIsGenerating(true);
    const userMessage = {
      role: 'user',
      content: input,
      timestamp: new Date(),
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInput('');

    // Create placeholder for streaming response
    const assistantMessageId = Date.now();
    const assistantMessage = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isStreaming: true,
    };
    setMessages(prev => [...prev, assistantMessage]);

    try {
      // Get conversation history
      const conversationHistory = messages?.filter(m => !m?.isError && !m?.type)?.map(m => ({ role: m?.role, content: m?.content }));

      await generateAppCode(
        input,
        (chunk) => {
          setMessages(prev => 
            prev?.map(msg => 
              msg?.id === assistantMessageId 
                ? { ...msg, content: msg?.content + chunk }
                : msg
            )
          );
        },
        conversationHistory
      );

      // Mark streaming as complete
      setMessages(prev => 
        prev?.map(msg => 
          msg?.id === assistantMessageId 
            ? { ...msg, isStreaming: false }
            : msg
        )
      );

      // Notify parent component
      if (onCodeGenerated) {
        const finalMessage = messages?.find(m => m?.id === assistantMessageId);
        onCodeGenerated(finalMessage?.content || '');
      }
    } catch (error) {
      const errorMessage = {
        role: 'assistant',
        content: `⚠️ Error generating code: ${error?.message}`,
        timestamp: new Date(),
        isError: true,
      };
      setMessages(prev => [...prev?.filter(m => m?.id !== assistantMessageId), errorMessage]);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setInput(`Create ${suggestion?.name}: ${suggestion?.description}`);
    setShowSuggestions(false);
  };

  const quickActions = [
    { label: 'Analyze Requirements', icon: Lightbulb, action: handleAnalyze, disabled: isAnalyzing || isGenerating },
    { label: 'Generate Code', icon: Code, action: handleGenerate, disabled: isGenerating || isAnalyzing },
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed right-0 top-0 h-full w-96 bg-white border-l border-gray-200 shadow-xl flex flex-col z-50">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="font-semibold text-gray-900">AI Assistant</h2>
            <p className="text-xs text-gray-500">Powered by GPT-5</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>
      </div>
      {/* Suggestions Panel */}
      {showSuggestions && suggestions?.length > 0 && (
        <div className="p-4 bg-blue-50 border-b border-blue-200">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-blue-900">App Ideas</h3>
            <button
              onClick={() => setShowSuggestions(false)}
              className="text-blue-600 hover:text-blue-800 text-xs"
            >
              Hide
            </button>
          </div>
          <div className="space-y-2">
            {suggestions?.map((suggestion, index) => (
              <button
                key={index}
                onClick={() => handleSuggestionClick(suggestion)}
                className="w-full text-left p-2 bg-white rounded-lg hover:bg-blue-100 transition-colors"
              >
                <p className="text-sm font-medium text-gray-900">{suggestion?.name}</p>
                <p className="text-xs text-gray-600">{suggestion?.description}</p>
              </button>
            ))}
          </div>
        </div>
      )}
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages?.map((message, index) => (
          <div
            key={index}
            className={`flex ${message?.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[85%] rounded-lg p-3 ${
                message?.role === 'user' ?'bg-blue-600 text-white'
                  : message?.isError
                  ? 'bg-red-50 text-red-900 border border-red-200'
                  : message?.type === 'analysis' ?'bg-purple-50 text-purple-900 border border-purple-200' :'bg-gray-100 text-gray-900'
              }`}
            >
              <div className="whitespace-pre-wrap text-sm">{message?.content}</div>
              {message?.isStreaming && (
                <div className="flex items-center gap-2 mt-2 text-xs opacity-70">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  <span>Generating...</span>
                </div>
              )}
              <div className={`text-xs mt-1 ${message?.role === 'user' ? 'text-blue-200' : 'text-gray-500'}`}>
                {message?.timestamp?.toLocaleTimeString()}
              </div>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>
      {/* Quick Actions */}
      <div className="px-4 py-2 border-t border-gray-200">
        <div className="flex gap-2">
          {quickActions?.map((action, index) => (
            <button
              key={index}
              onClick={action?.action}
              disabled={action?.disabled}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-sm font-medium text-gray-700 transition-colors"
            >
              <action.icon className="w-4 h-4" />
              {action?.label}
            </button>
          ))}
        </div>
      </div>
      {/* Suggestion Categories */}
      <div className="px-4 py-2 border-t border-gray-200">
        <p className="text-xs text-gray-500 mb-2">Need inspiration?</p>
        <div className="flex gap-2 flex-wrap">
          {['e-commerce', 'social', 'productivity', 'portfolio']?.map((category) => (
            <button
              key={category}
              onClick={() => loadSuggestions(category)}
              className="px-3 py-1 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-full text-xs font-medium transition-colors"
            >
              {category}
            </button>
          ))}
        </div>
      </div>
      {/* Input */}
      <div className="p-4 border-t border-gray-200">
        <div className="flex gap-2">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e?.target?.value)}
            onKeyDown={(e) => {
              if (e?.key === 'Enter' && !e?.shiftKey) {
                e?.preventDefault();
                handleGenerate();
              }
            }}
            placeholder="Describe your app... (Shift+Enter for new line)"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            rows={1}
            disabled={isGenerating || isAnalyzing}
          />
          <button
            onClick={handleGenerate}
            disabled={!input?.trim() || isGenerating || isAnalyzing}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
          >
            {isGenerating || isAnalyzing ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Press Enter to send, Shift+Enter for new line
        </p>
      </div>
    </div>
  );
};

export default AIChatPanel;